import { useMemo } from 'react';
import { useTheme } from '@mui/material';

import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import EditNoteIcon from '@mui/icons-material/EditNote';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import BreakdownBlock, { formatDate } from '../breakdownBlock/breakdownBlock';

// Icon per cancellation category. Two vocabularies live here on purpose: the five
// types of the Gerente's "Tipo Cancelamento" combo, written exactly as they are
// registered, and the labels the API still derives from the free-text reason for
// the cancellations registered before the combo existed (Apr/2026 on). As the
// older periods get typed, the second half stops appearing on its own.
const REASON_ICONS = {
  // Gerente — TB_TIPO_CANCELAMENTO
  'MUDOU DE SISTEMA': <SwapHorizIcon />,
  'EMPRESA FECHOU': <StorefrontIcon />,
  'SITUAÇÃO FINANCEIRA': <MoneyOffIcon />,
  'FALHA DA SIRRUS': <SentimentDissatisfiedIcon />,
  'NÃO CONTABILIZADOS': <AssignmentLateIcon />,

  // Derived from CANCELA_MOTIVO (cancelReasonServices)
  'Trocou de sistema / concorrência': <SwapHorizIcon />,
  'Empresa fechou / encerrou atividades': <StorefrontIcon />,
  'Inativo / parou de usar': <HourglassEmptyIcon />,
  'Inadimplência': <MoneyOffIcon />,
  'Venda não concretizada': <RemoveShoppingCartIcon />,
  'Insatisfação com o sistema / atendimento': <SentimentDissatisfiedIcon />,
  'Vendeu / trocou de proprietário': <BusinessCenterIcon />,
  'Cancelamento interno / cadastro': <AssignmentLateIcon />,
  'Poucos / sem funcionários': <PeopleOutlineIcon />,
  'Sem contato com o cliente': <PhoneDisabledIcon />,
  'Sem interesse / não precisa mais': <ThumbDownAltIcon />,
  'Custo / valor da manutenção': <PriceChangeIcon />,
  'Padronização do grupo / matriz': <AccountTreeIcon />,
  'Voltou ao controle manual': <EditNoteIcon />,
  'A pedido do cliente': <RecordVoiceOverIcon />,
  'Sem motivo informado': <HelpOutlineIcon />,
};

// Turns an API group ({ description, count, maintenance, items }) into a category:
// reason -> cancelled client -> the reason as it was typed in the Gerente.
// The donut reads the maintenance lost, so the count travels on the row's name.
const toCategories = (groups) =>
  (groups || []).map((group) => ({
    id: group.description,
    colorKey: group.description,
    name: `${group.description} (${group.count})`,
    icon: REASON_ICONS[group.description] || <MoreHorizIcon />,
    value: group.maintenance || 0,
    children: (group.items || []).map((client) => ({
      // A suspension is flagged on the row: the client stopped paying but has not
      // been cancelled yet
      name: [
        formatDate(client.dataCancelamento) || 'Sem data',
        client.tradeName || client.razaoSocial || 'Sem nome',
        client.situacao === 'Suspenso' ? '(suspenso)' : null,
      ].filter(Boolean).join(' · '),
      value: client.valorManutencao || 0,
      // The reason is the explanation, never the grouping. It is also what makes a
      // wrong category auditable — and rows without a type in the Gerente say so,
      // because those are the ones whose category was inferred from this text.
      entries: [{
        id: client.id,
        primary: client.motivoOriginal || 'Motivo não informado no Gerente',
        secondary: [
          client.situacao,
          client.segmento,
          client.cpfCnpj,
          client.tipoGerente ? null : 'sem tipo no Gerente',
        ].filter(Boolean).join(' · '),
        value: client.valorManutencao || 0,
      }],
    })),
  }));

export default function CanceledBreakdown({ canceled }) {
  const theme = useTheme();
  const mode = theme.palette.mode === 'dark' ? 'dark' : 'light';

  const reasonCategories = useMemo(
    () => toCategories(canceled?.byReason?.groups),
    [canceled]
  );

  return (
    <BreakdownBlock
      title="Cancelamento por Motivo"
      categories={reasonCategories}
      mode={mode}
      emptyLabel="Sem cancelamentos no período"
      totalLabel="Manutenção perdida"
      // A client with no maintenance contract still belongs on the list
      keepZeroValues
    />
  );
}
